import type { ThreadColumn, ThreadRow } from "../thread-design-system";
import type { V3PivotView, V3ProvenanceGraph } from "./types";

const CONDITION_FIELD_IDS = new Set(["alpha", "beta", "mach", "re", "reynolds", "rpm", "thrust", "conditionKey", "condition_key"]);

function valueKey(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (Math.abs(value) >= 10000 || (Math.abs(value) < 0.001 && value !== 0)) return value.toExponential(2);
    return Number(value.toFixed(6)).toString();
  }
  if (value == null || value === "") return "__empty__";
  return String(value);
}

function rowHasStableId(row: ThreadRow): boolean {
  return typeof row.runId === "string" && row.runId.trim().length > 0;
}

function conditionColumns(columns: readonly ThreadColumn[]): ThreadColumn[] {
  return columns.filter((column) => column.kind === "condition" || CONDITION_FIELD_IDS.has(column.id));
}

function uniqueValues(rows: readonly ThreadRow[], fieldId: string): string[] {
  return [...new Set(rows.map((row) => valueKey(row[fieldId])))];
}

function heldFieldsForGroup(columns: readonly ThreadColumn[], rows: readonly ThreadRow[], sweepFieldId: string): V3PivotView["heldFields"] {
  return columns
    .filter((column) => column.id !== sweepFieldId)
    .map((column) => {
      const values = uniqueValues(rows, column.id);
      return values.length === 1 ? { id: column.id, label: column.label, value: values[0]! } : null;
    })
    .filter((item): item is V3PivotView["heldFields"][number] => Boolean(item));
}

function unavailable(rows: readonly ThreadRow[]): V3PivotView {
  return {
    id: "unavailable",
    label: "No stable pivot view",
    kind: "unavailable",
    heldFields: [],
    compatibleRowIds: [],
    incompatibleRowIds: rows.map((row) => row.runId).filter(Boolean),
    unavailableReason: "Table view needs at least two compatible rows with stable row IDs.",
  };
}

export function derivePivotCandidates(
  rows: readonly ThreadRow[],
  columns: readonly ThreadColumn[],
  provenance: V3ProvenanceGraph,
  selectedNodeIds: Iterable<string> = [],
): V3PivotView[] {
  const stableRows = rows.filter(rowHasStableId);
  if (stableRows.length < 2) return [unavailable(rows)];

  const candidates: V3PivotView[] = [];
  const conditions = [...conditionColumns(columns)];
  if (!conditions.some((column) => column.id === "conditionKey" || column.id === "condition_key") && stableRows.some((row) => row.conditionKey)) {
    conditions.push({ id: "conditionKey", label: "condition_key", kind: "condition" });
  }

  for (const sweepField of conditions) {
    const sweepValues = uniqueValues(stableRows, sweepField.id).filter((value) => value !== "__empty__");
    if (sweepValues.length < 2) continue;
    const heldColumns = conditions.filter((column) => column.id !== sweepField.id);
    const groups = new Map<string, ThreadRow[]>();
    for (const row of stableRows) {
      const heldKey = heldColumns.map((column) => `${column.id}=${valueKey(row[column.id])}`).join("|");
      const group = groups.get(heldKey) ?? [];
      group.push(row);
      groups.set(heldKey, group);
    }
    const best = [...groups.values()]
      .filter((group) => uniqueValues(group, sweepField.id).length >= 2)
      .sort((a, b) => b.length - a.length)[0];
    if (!best || best.length < 2) continue;
    const compatibleRowIds = best.map((row) => row.runId);
    const compatible = new Set(compatibleRowIds);
    const heldFields = heldFieldsForGroup(heldColumns, best, sweepField.id);
    const heldLabel = heldFields.length
      ? heldFields.map((field) => `${field.label} ${field.value}`).join(" · ")
      : "shared lineage";
    candidates.push({
      id: `pivot:${sweepField.id}:${heldFields.map((field) => `${field.id}-${field.value}`).join(":") || "all"}`,
      label: `${heldLabel} · ${sweepField.label} sweep`,
      kind: "condition-sweep",
      heldFields,
      sweepField: { id: sweepField.id, label: sweepField.label },
      compatibleRowIds,
      incompatibleRowIds: stableRows.filter((row) => !compatible.has(row.runId)).map((row) => row.runId),
    });
  }

  const conditionKeyColumn = conditions.find((column) => column.id === "conditionKey" || column.id === "condition_key");
  if (conditionKeyColumn) {
    const byConditionKey = new Map<string, ThreadRow[]>();
    for (const row of stableRows) {
      const key = valueKey(row[conditionKeyColumn.id] ?? row.conditionKey);
      if (key === "__empty__") continue;
      const group = byConditionKey.get(key) ?? [];
      group.push(row);
      byConditionKey.set(key, group);
    }
    const best = [...byConditionKey.entries()].filter(([, group]) => group.length >= 2).sort((a, b) => b[1].length - a[1].length)[0];
    if (best) {
      const [key, group] = best;
      const compatibleRowIds = group.map((row) => row.runId);
      const compatible = new Set(compatibleRowIds);
      candidates.push({
        id: `pivot:condition-key:${key}`,
        label: `condition_key ${key}`,
        kind: "condition-key",
        heldFields: [{ id: conditionKeyColumn.id, label: conditionKeyColumn.label, value: key }],
        compatibleRowIds,
        incompatibleRowIds: stableRows.filter((row) => !compatible.has(row.runId)).map((row) => row.runId),
      });
    }
  }

  const selectedIds = new Set(selectedNodeIds);
  if (selectedIds.size) {
    const rowIds = provenance.rows
      .filter((row) => row.sourceNodeIds.some((nodeId) => selectedIds.has(nodeId)) || row.cells.some((cell) => cell.sourceNodeIds.some((nodeId) => selectedIds.has(nodeId))))
      .map((row) => row.runId);
    const uniqueRowIds = [...new Set(rowIds)];
    if (uniqueRowIds.length >= 2) {
      const compatible = new Set(uniqueRowIds);
      candidates.push({
        id: `pivot:selected-lineage:${[...selectedIds].slice(0, 3).join(":")}`,
        label: "selected lineage pivot",
        kind: "selected-lineage",
        heldFields: [],
        compatibleRowIds: uniqueRowIds,
        incompatibleRowIds: stableRows.filter((row) => !compatible.has(row.runId)).map((row) => row.runId),
      });
    }
  }

  return candidates.length ? candidates : [unavailable(rows)];
}

export function pivotIsAvailable(pivot: V3PivotView | null | undefined): pivot is V3PivotView {
  return Boolean(pivot && !pivot.unavailableReason && pivot.compatibleRowIds.length >= 2);
}
